import { CenteredDialog } from '../../components/ui/centered-dialog';
import { useState, useEffect, type ReactElement } from 'react';
import { AlertTriangle, FileText } from 'lucide-react';
import type { DocumentoFiscalEntradaRow, AnaliseImpactoEstornoResult, EscopoEstorno } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { atalhoComPermissao, comNotaDePendencia, linkCadastrarRoteiroDoLote, permissoesBastam, type AtalhoComPermissao } from '../../lib/roteiros/cadastro-peca';
import { interpretarErroApi } from '../../lib/vendas/erro-api';
import { obterSessaoAtual, type ObterSessaoResult } from '../../lib/session';
import { deveVerificarPendencia, mensagemPendencia } from '../../lib/pendencia-remessa';
import type { DadosFinanceiroBadgeInput } from '../../lib/financeiro-badge';
import { RetornoPanel } from './retorno-panel';
import { BannerSucessoDeRemessas, bannerDoEstorno, bannerDoRecebimento, type AtalhoDoBanner, type BannerSucessoRemessas } from './remessas-banner';
import { RemessasAcoes, podeGerenciarRemessas } from './remessas-acoes';
import {
  AvisoEstornoDoLink,
  BotaoEstornarRemessa,
  SeloEstorno,
  estornoDaUrl,
  podeEstornarRemessas,
  urlImpactoDoEstorno,
} from './remessas-estorno';
import { AcoesPvDaNota } from './conferencia-pv/acoes-pv-da-nota';
import { ItemDoPvSelect } from './conferencia-pv/item-do-pv-select';
import { confirmarRecebimentoDaNota, useCandidatosDaNota } from './conferencia-pv/use-recebimento-pv';
import { TablePagination } from '../../components/ui/table-pagination';
import { ResponsiveTableRow, useTabelaEmContextoTablet } from '../../components/ui/responsive-table-row';
import { StatusIndicator } from '../../components/ui/status-indicator';
import {
  MENSAGEM_ITEM_PV_SEM_MATERIAL,
  MENSAGEM_NOTA_JA_CONFIRMADA,
  MENSAGEM_NOTA_LINK_INVALIDO,
  ROTULO_CIENTE_NOVO_RECEBIMENTO,
  candidatosSemMaterialEscolhidos,
  conferenciaBloqueada,
  mensagemResultadoItem,
  notaDoLink,
  notaJaConfirmada,
  type AlocacaoEmEdicao,
  type ContextoConfirmacao,
} from '../../lib/vendas/recebimento-pv';
import { CampoSelect } from '../../components/ui/campo-select';
import { LookupCombobox } from '../../components/ui/lookup-combobox';
import { RemessaCadastroPecaDialog } from './remessa-cadastro-peca-dialog';

export interface RemessaRow {
  readonly id: string;
  readonly clienteId: string;
  readonly itemId: string;
  readonly loteClienteRef: string;
  readonly documentoEntrada: string | null;
  readonly qtdDocumental: number;
  readonly qtdFisica: number;
  readonly divergencia: number;
  readonly createdAt: string;
  /** L-65: documento fiscal da NF recebida na remessa (o estorno é do documento); `null` na remessa manual. */
  readonly documentoFiscalId: string | null;
  /** L-66: quantidade já estornada do recebimento; `qtdFisica` já vem líquida. */
  readonly quantidadeEstornada: number;
  readonly estornada: boolean;
}

function mensagemDeErro(valor: unknown): string | null {
  if (typeof valor !== 'object' || valor === null) return null;
  const resposta = valor as Record<string, unknown>;
  return typeof resposta.message === 'string' ? resposta.message : null;
}

function extrairDadosFinanceiros(valor: unknown): DadosFinanceiroBadgeInput | null {
  if (typeof valor !== 'object' || valor === null) return null;
  const dados = valor as Record<string, unknown>;
  const situacao = dados.situacaoCredito;
  const contas = dados.contasReceber;
  if (situacao !== undefined && situacao !== null && typeof situacao !== 'object') return null;
  if (contas !== undefined && contas !== null && typeof contas !== 'object') return null;
  if (dados.inadimplente !== undefined && dados.inadimplente !== null && typeof dados.inadimplente !== 'boolean') return null;
  return dados;
}

export function RemessasPage(): ReactElement {
  const tabelaEmTablet = useTabelaEmContextoTablet();
  const [modoEntrada] = useState(() => new URLSearchParams(window.location.search).get('entrada'));
  const [remessas, setRemessas] = useState<RemessaRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  // Sessão e aviso de inadimplência (D-02, D-04)
  const [sessao, setSessao] = useState<ObterSessaoResult | null>(null);
  const [avisoPendencia, setAvisoPendencia] = useState<string | null>(null);

  // Modal de Nova Remessa Manual
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [clienteId, setClienteId] = useState<string>('');
  const [itemId, setItemId] = useState<string>('');
  const [itemPorCodigo, setItemPorCodigo] = useState(false);
  const [cadastroPecaOpen, setCadastroPecaOpen] = useState(false);
  const [loteClienteRef, setLoteClienteRef] = useState<string>('');
  const [documentoEntrada, setDocumentoEntrada] = useState<string>('');
  const [qtdDocumental, setQtdDocumental] = useState<string>('');
  const [qtdFisica, setQtdFisica] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Modal de Importação de NF-e (XML/Chave — Fase 10 Tracer)
  const [nfeModalOpen, setNfeModalOpen] = useState<boolean>(() => modoEntrada === 'chave' || modoEntrada === 'xml');
  const [nfeChave, setNfeChave] = useState<string>('');
  const [nfeXmlContent, setNfeXmlContent] = useState<string>('');
  const [nfeRascunho, setNfeRascunho] = useState<DocumentoFiscalEntradaRow | null>(null);
  const [nfeLoading, setNfeLoading] = useState<boolean>(false);
  const [nfeError, setNfeError] = useState<string | null>(null);
  const [conferencias, setConferencias] = useState<Record<string, { qtdFisica: string; materialId: string; loteClienteRef: string }>>({});
  // Banner de sucesso (recebimento ou estorno). Fase 12 (NFE-06): uma mensagem por item da nota; D-25b: itens recebidos sem
  // roteiro vigente ganham o atalho "Cadastrar roteiro". Um único estado: trocar o banner nunca deixa atalhos do anterior (P3-WR-01).
  const [banner, setBanner] = useState<BannerSucessoRemessas | null>(null);
  const [submitAtalho, setSubmitAtalho] = useState<AtalhoComPermissao | null>(null);
  // Fase 12 (NFE-05): escolha por item da nota — item de PV, 'Dividir entre itens…' ou '' (sem PV, caminho legado).
  // A FIFO vem pré-selecionada da API; justificativas de excedente e divisões ficam preservadas se a confirmação falhar.
  const candidatosPv = useCandidatosDaNota(nfeRascunho?.id ?? null);
  const [pvSelecao, setPvSelecao] = useState<Record<string, string>>({});
  const [pvJustificativas, setPvJustificativas] = useState<Record<string, string>>({});
  const [pvDivisoes, setPvDivisoes] = useState<Record<string, readonly AlocacaoEmEdicao[]>>({});
  // WR-D2-01: trocar de nota zera a escolha; a seleção inicial (FIFO) entra só quando as sugestões TERMINAM de carregar e
  // NUNCA sobrescreve o que o usuário já escolheu/dividiu/justificou (`{ ...inicial, ...atual }`).
  const notaDaConferenciaId = nfeRascunho?.id ?? null;
  useEffect(() => {
    setPvSelecao({});
    setPvDivisoes({});
    setPvJustificativas({});
  }, [notaDaConferenciaId]);
  useEffect(() => {
    if (candidatosPv.carregando) return;
    setPvSelecao((atual) => ({ ...candidatosPv.selecaoInicial, ...atual }));
    setPvDivisoes((atual) => ({ ...candidatosPv.divisoesIniciais, ...atual }));
  }, [candidatosPv.carregando, candidatosPv.selecaoInicial, candidatosPv.divisoesIniciais]);
  const aguardandoSugestoes = candidatosPv.carregando;

  const itensConferidos = () =>
    (nfeRascunho?.itens ?? []).map((item) => {
      const conf = conferencias[item.id];
      return {
        documentoFiscalItemId: item.id,
        quantidadeFisica: parseFloat(conf?.qtdFisica || String(item.quantidadeDocumental)),
        materialId: conf?.materialId || item.materialId || undefined,
        loteClienteRef: conf?.loteClienteRef || undefined,
      };
    });
  const contextoPv = (): ContextoConfirmacao => ({
    candidatosPorItem: candidatosPv.candidatosPorItem,
    justificativas: pvJustificativas,
    divisoes: pvDivisoes,
  });
  const confirmacaoBloqueada = conferenciaBloqueada(pvSelecao, itensConferidos(), contextoPv());
  const itensPvSemMaterial = candidatosSemMaterialEscolhidos(pvSelecao, itensConferidos(), contextoPv());
  const notaConfirmada = notaJaConfirmada(nfeRascunho?.estado);
  // Nota já CONFIRMADA aceita recebimento parcial adicional (servidor): pede ciência explícita antes de confirmar de novo.
  const [cienteNovoRecebimento, setCienteNovoRecebimento] = useState(false);
  useEffect(() => {
    setCienteNovoRecebimento(false);
  }, [notaDaConferenciaId]);

  // Painel de Retorno
  const [retornoOpen, setRetornoOpen] = useState<boolean>(false);
  const [selectedClienteForRetorno, setSelectedClienteForRetorno] = useState<string | undefined>(undefined);

  // Modal de Estorno Compensatório (REM-08 / D-30..D-33)
  const [estornoModalOpen, setEstornoModalOpen] = useState<boolean>(false);
  const [estornoDocId, setEstornoDocId] = useState<string | null>(null);
  const [impacto, setImpacto] = useState<AnaliseImpactoEstornoResult | null>(null);
  const [impactoLoading, setImpactoLoading] = useState<boolean>(false);
  const [impactoError, setImpactoError] = useState<string | null>(null);
  const [estornoEscopo, setEstornoEscopo] = useState<EscopoEstorno>('TOTAL');
  const [estornoMotivo, setEstornoMotivo] = useState<string>('');
  const [estornoAprovadorUserId, setEstornoAprovadorUserId] = useState<string>('');
  const [estornoSubmitting, setEstornoSubmitting] = useState<boolean>(false);
  const [estornoSubmitError, setEstornoSubmitError] = useState<string | null>(null);
  const [estornoManualId, setEstornoManualId] = useState<string | null>(null);
  const [impactoManual, setImpactoManual] = useState<{ elegivel: boolean; estornada: boolean; saldo: number; bloqueios: readonly string[] } | null>(null);
  const [motivoEstornoManual, setMotivoEstornoManual] = useState('');
  const [erroEstornoManual, setErroEstornoManual] = useState<string | null>(null);

  // L-65 (17.1-28): o estorno é do DOCUMENTO FISCAL do recebimento. A linha da lista traz `documentoFiscalId`; a API de
  // impacto e de estorno não conhecem o id da remessa (antes, a tela mandava `rem.id` e a análise respondia "não encontrado").
  const handleAbrirEstorno = async (docId: string | null) => {
    if (docId === null) return;
    setEstornoDocId(docId);
    setEstornoModalOpen(true);
    setImpactoLoading(true);
    setImpactoError(null);
    setEstornoMotivo('');
    setEstornoAprovadorUserId('');
    setEstornoEscopo('TOTAL');
    setEstornoSubmitError(null);
    try {
      const res = await fetch(urlImpactoDoEstorno(docId), {
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json() as { message?: string };
        throw new Error(err.message || 'Falha ao analisar impacto do estorno.');
      }
      const data = (await res.json()) as AnaliseImpactoEstornoResult;
      setImpacto(data);
    } catch (err) {
      setImpactoError(err instanceof Error ? err.message : 'Erro ao analisar impacto do estorno.');
    } finally {
      setImpactoLoading(false);
    }
  };

  const handleExecutarEstorno = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!estornoDocId || !impacto) return;
    setEstornoSubmitting(true);
    setEstornoSubmitError(null);
    try {
      const res = await apiMutate('/api/v1/remessas/estorno', {
        method: 'POST',
        body: {
          documentoFiscalId: estornoDocId,
          escopo: estornoEscopo,
          motivo: estornoMotivo.trim(),
          aprovadorUserId: impacto.exigeAprovacaoReforcada ? estornoAprovadorUserId.trim() : null,
        },
      });
      if (!res.ok) {
        const err = await res.json() as { message?: string };
        throw new Error(err.message || 'Erro ao processar estorno.');
      }
      setBanner(bannerDoEstorno());
      setEstornoModalOpen(false);
      void fetchRemessas();
    } catch (err) {
      setEstornoSubmitError(err instanceof Error ? err.message : 'Erro ao processar estorno.');
    } finally {
      setEstornoSubmitting(false);
    }
  };

  const abrirEstornoManual = async (remessaId: string): Promise<void> => {
    setEstornoManualId(remessaId); setImpactoManual(null); setMotivoEstornoManual(''); setErroEstornoManual(null);
    try {
      const res = await fetch(`/api/v1/remessas/${encodeURIComponent(remessaId)}/estorno-manual/impacto`, { credentials: 'include' });
      const dados = await res.json() as { message?: string; elegivel?: boolean; estornada?: boolean; saldo?: number; bloqueios?: readonly string[] };
      if (!res.ok) throw new Error(dados.message || 'Não foi possível analisar a entrada manual.');
      setImpactoManual({ elegivel: dados.elegivel === true, estornada: dados.estornada === true, saldo: Number(dados.saldo ?? 0), bloqueios: dados.bloqueios ?? [] });
    } catch (falha) { setErroEstornoManual(falha instanceof Error ? falha.message : 'Não foi possível analisar a entrada manual.'); }
  };

  const executarEstornoManual = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!estornoManualId || !motivoEstornoManual.trim()) return;
    setErroEstornoManual(null);
    try {
      const res = await apiMutate(`/api/v1/remessas/${encodeURIComponent(estornoManualId)}/estorno-manual`, { method: 'POST', body: { motivo: motivoEstornoManual.trim() } });
      const dados = await res.json().catch(() => ({})) as { message?: string };
      if (!res.ok) throw new Error(dados.message || 'Não foi possível estornar a entrada manual.');
      setEstornoManualId(null); setBanner({ mensagem: 'Entrada manual estornada por movimento compensatório.', detalhes: [], atalhos: [] }); void fetchRemessas();
    } catch (falha) { setErroEstornoManual(falha instanceof Error ? falha.message : 'Não foi possível estornar a entrada manual.'); }
  };

  const fetchRemessas = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/remessas?page=${page}&limit=${pageSize}`);
      if (!res.ok) {
        throw new Error('Falha ao carregar lista de remessas.');
      }
      const data = (await res.json()) as { data: RemessaRow[]; meta: { total: number } };
      setRemessas(data.data ?? []);
      setTotal(data.meta?.total ?? data.data?.length ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar remessas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRemessas();
  }, [page, pageSize]);

  useEffect(() => {
    void obterSessaoAtual().then(setSessao);
  }, []);

  // L-63/L-64: `?estornar=<documentoFiscalId>` (link da pendência CANCELAMENTO_NFE) abre o diálogo de estorno desse documento para quem
  // tem remessas:estornar; quem não tem vê a orientação. O parâmetro só pré-seleciona o documento: a análise de impacto, a justificativa
  // e a confirmação continuam obrigatórias e a API segue exigindo a permissão (T-17.1-28-01/02).
  const [estornoDoLink] = useState(() => estornoDaUrl(window.location.search));
  const permissoesParaEstorno: readonly string[] | null = sessao?.autenticado ? sessao.usuario.permissions : null;
  const [estornoDoLinkAberto, setEstornoDoLinkAberto] = useState(false);
  useEffect(() => {
    if (estornoDoLinkAberto || estornoDoLink.situacao !== 'documento' || !podeEstornarRemessas(permissoesParaEstorno)) return;
    setEstornoDoLinkAberto(true);
    void handleAbrirEstorno(estornoDoLink.documentoFiscalId);
  }, [estornoDoLink, permissoesParaEstorno, estornoDoLinkAberto]);

  // L-51: criar remessa, novo retorno, importar NF-e e retornar só com remessas:gerenciar; a consulta basta para ver a lista.
  const permissoesDaSessao: readonly string[] | null = sessao?.autenticado ? sessao.usuario.permissions : null;
  const podeGerenciar = podeGerenciarRemessas(permissoesDaSessao);

  useEffect(() => {
    if (!modalOpen || !clienteId.trim() || !deveVerificarPendencia(sessao?.autenticado ? sessao.usuario : null)) {
      setAvisoPendencia(null);
      return;
    }

    const abort = new AbortController();
    setAvisoPendencia(null);
    const timer = setTimeout(() => void (async () => {
      try {
        const res = await fetch(`/api/v1/cadastros/clientes/${encodeURIComponent(clienteId.trim())}/financeiro`, {
          credentials: 'include',
          signal: abort.signal,
        });
        if (res.ok) {
          const finData: unknown = await res.json();
          const msg = mensagemPendencia(extrairDadosFinanceiros(finData));
          if (!abort.signal.aborted) setAvisoPendencia(msg);
        } else {
          if (!abort.signal.aborted) setAvisoPendencia(null);
        }
      } catch {
        if (!abort.signal.aborted) setAvisoPendencia(null);
      }
    })(), 400);

    return () => { clearTimeout(timer); abort.abort(); };
  }, [clienteId, modalOpen, sessao]);

  const numDoc = parseInt(qtdDocumental || '0', 10);
  const numFis = parseInt(qtdFisica || '0', 10);
  const temDivergencia = qtdDocumental !== '' && qtdFisica !== '' && numDoc !== numFis;
  const delta = numFis - numDoc;

  const handleCriarRemessa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clienteId || !itemId || !loteClienteRef || !qtdDocumental || !qtdFisica) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmitAtalho(null);
    try {
      const res = await apiMutate('/api/v1/remessas', {
        method: 'POST',
        body: {
          clienteId,
          itemId,
          loteClienteRef,
          documentoEntrada: documentoEntrada.trim() || undefined,
          qtdDocumental: numDoc,
          qtdFisica: numFis,
        },
      });

      if (!res.ok) {
        const errData: unknown = await res.json().catch(() => null);
        // D-25: item sem cadastro devolve o atalho "Cadastrar peça" pelo código do cliente; a pendência já ficou registrada.
        const atalho = atalhoComPermissao(interpretarErroApi(res.status, errData));
        setSubmitAtalho(atalho);
        const mensagem = mensagemDeErro(errData) ?? 'Erro ao registrar remessa';
        throw new Error(atalho ? comNotaDePendencia(mensagem) : mensagem);
      }

      setModalOpen(false);
      const recebida = await res.json() as { lote: { id: string } };
      setBanner({ mensagem: 'Remessa registrada. Continue pela preparação da produção.', detalhes: [], atalhos: [{ chave: recebida.lote.id, texto: 'Lote recebido', href: `/producao/ordens/preparar?loteId=${encodeURIComponent(recebida.lote.id)}`, rotulo: 'Preparar produção', permissao: 'producao:consultar' }] });
      setClienteId('');
      setItemId('');
      setItemPorCodigo(false);
      setLoteClienteRef('');
      setDocumentoEntrada('');
      setQtdDocumental('');
      setQtdFisica('');
      void fetchRemessas();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setSubmitting(false);
    }
  };

  const aplicarRascunho = (data: DocumentoFiscalEntradaRow) => {
    setNfeRascunho(data);

    const initialConfs: Record<string, { qtdFisica: string; materialId: string; loteClienteRef: string }> = {};
    (data.itens ?? []).forEach((item) => {
      initialConfs[item.id] = {
        qtdFisica: String(item.quantidadeDocumental),
        materialId: item.materialId ?? item.descricao,
        loteClienteRef: item.loteClienteRef ?? '',
      };
    });
    setConferencias(initialConfs);
  };

  // PV-04: `/remessas?nota={id}` reabre a conferência da mesma NF (volta do formulário de PV), reaproveitando
  // o carregamento existente (GET /documentos-fiscais/:id) sem duplicar estado fiscal.
  useEffect(() => {
    const nota = notaDoLink(window.location.search);
    if (nota.tipo === 'ausente') return;
    setNfeModalOpen(true);
    if (nota.tipo === 'invalido') {
      // Id que não é UUID: mensagem clara em vez de erro cru dentro do formulário de importação.
      setNfeError(MENSAGEM_NOTA_LINK_INVALIDO);
      limparParametroNota();
      return;
    }
    const notaId = nota.id;
    let vivo = true;
    setNfeLoading(true);
    setNfeError(null);
    void (async () => {
      try {
        const res = await fetch(`/api/v1/documentos-fiscais/${encodeURIComponent(notaId)}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Não foi possível reabrir a conferência da nota.');
        const data = (await res.json()) as DocumentoFiscalEntradaRow;
        if (vivo) aplicarRascunho(data);
      } catch (err) {
        if (vivo) setNfeError(err instanceof Error ? err.message : 'Não foi possível reabrir a conferência da nota.');
      } finally {
        if (vivo) setNfeLoading(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  /** Tira o `?nota=` da URL (fechar/confirmar): recarregar a página não reabre a conferência já resolvida (WR-D2-06). */
  function limparParametroNota(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('nota')) return;
    url.searchParams.delete('nota');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  const fecharModalNfe = () => {
    setNfeModalOpen(false);
    limparParametroNota();
  };

  const handleImportarNfe = async (e: React.FormEvent) => {
    e.preventDefault();
    setNfeLoading(true);
    setNfeError(null);
    setBanner(null);
    try {
      let body: Record<string, unknown> = {};
      if (nfeXmlContent.trim()) {
        const base64 = btoa(unescape(encodeURIComponent(nfeXmlContent.trim())));
        body = { xmlBase64: base64 };
      } else if (nfeChave.trim()) {
        body = { chaveNfe: nfeChave.trim() };
      } else {
        throw new Error('Informe o XML ou a chave de 44 dígitos.');
      }

      const res = await apiMutate('/api/v1/documentos-fiscais/rascunho', {
        method: 'POST',
        body,
      });

      if (!res.ok) {
        const errData: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDeErro(errData) ?? 'Falha ao processar documento fiscal.');
      }

      aplicarRascunho((await res.json()) as DocumentoFiscalEntradaRow);
    } catch (err) {
      setNfeError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setNfeLoading(false);
    }
  };

  const handleConfirmarRecebimento = async () => {
    if (!nfeRascunho) return;
    setNfeLoading(true);
    setNfeError(null);
    try {
      const itens = itensConferidos();
      const res = await confirmarRecebimentoDaNota(nfeRascunho.id, itens, pvSelecao, contextoPv());

      // Falha: a modal continua aberta com seleção, divisões e justificativas preservadas (role="alert" abaixo).
      if (!res.ok) throw new Error(res.message);

      const detalhes: string[] = [];
      const atalhos: AtalhoDoBanner[] = [];
      if (res.resultado) {
        for (const resultado of res.resultado.itens) {
          const numeroItem = (nfeRascunho.itens ?? []).find((item) => item.id === resultado.documentoFiscalItemId)?.numeroItem;
          const mensagem = mensagemResultadoItem({ opNumero: resultado.opNumero, semRoteiro: resultado.semRoteiro, modoObservacao: res.resultado.modoObservacao });
          detalhes.push(numeroItem !== undefined ? `Item ${numeroItem}: ${mensagem}` : mensagem);
          atalhos.push({ chave: `preparar-${resultado.loteId}`, texto: resultado.loteInternoPieltec, href: `/producao/ordens/preparar?loteId=${encodeURIComponent(resultado.loteId)}`, rotulo: resultado.opNumero ? `Abrir OP-${resultado.opNumero}` : 'Preparar produção', permissao: 'producao:consultar' });
          if (resultado.semRoteiro) atalhos.push({ chave: resultado.loteId, texto: numeroItem !== undefined ? `Item ${numeroItem}` : resultado.loteInternoPieltec, href: linkCadastrarRoteiroDoLote(resultado.loteId) });
        }
        if (res.resultado.itens.length === 0 && res.resultado.modoObservacao) detalhes.push(mensagemResultadoItem({ modoObservacao: true }));
      }
      setBanner(bannerDoRecebimento(detalhes, atalhos));
      setNfeRascunho(null);
      setNfeChave('');
      setNfeXmlContent('');
      limparParametroNota();
      void fetchRemessas();
    } catch (err) {
      setNfeError(err instanceof Error ? err.message : 'Erro ao confirmar');
    } finally {
      setNfeLoading(false);
    }
  };

  return (
    <div className="remessas-page min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Entrada de Remessas e Retorno</h1>
            <p className="text-sm text-slate-500 mt-1">
              {total === 1 ? '1 remessa cadastrada' : `${total} remessas cadastradas`} — controle fiscal, físico e lotes
            </p>
          </div>
          <RemessasAcoes
            permissoes={permissoesDaSessao}
            onNovoRetorno={() => {
              setSelectedClienteForRetorno(undefined);
              setRetornoOpen(true);
            }}
            onImportarNfe={() => {
              setNfeError(null);
              setBanner(null);
              setNfeRascunho(null);
              setNfeModalOpen(true);
            }}
            onNovaRemessaManual={() => {
              setSubmitError(null);
              setAvisoPendencia(null);
              setModalOpen(true);
            }}
          />
        </header>

        <AvisoEstornoDoLink permissoes={permissoesParaEstorno} estorno={estornoDoLink} />

        <BannerSucessoDeRemessas banner={banner} permissoes={permissoesDaSessao} />

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium" role="alert">
            {error}
          </div>
        )}

        {/* Tabela de Remessas D5 */}
        <main className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">Carregando remessas...</div>
          ) : remessas.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              {podeGerenciar
                ? 'Nenhuma remessa registrada. Use Importar NF de remessa ou Nova Remessa Manual para registrar a entrada do material do cliente.'
                : 'Nenhuma remessa registrada.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-3">Cliente</th>
                    <th className="p-3">Item / Material</th>
                    {!tabelaEmTablet && <th className="p-3">Lote do Cliente</th>}
                    {!tabelaEmTablet && <th className="p-3">Doc. Entrada</th>}
                    {!tabelaEmTablet && <th className="p-3 text-right">Qtd Doc.</th>}
                    <th className="p-3 text-right">Qtd Física</th>
                    {!tabelaEmTablet && <th className="p-3">Divergência</th>}
                    {!tabelaEmTablet && <th className="p-3">Data</th>}
                    <th className="p-3 text-center">Ações</th>
                    {tabelaEmTablet && <th className="p-2 text-right"><span className="sr-only">Detalhes</span></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {remessas.map((rem) => {
                    // L-66: remessa com recebimento estornado não mostra divergência (a quantidade líquida é a que sobrou).
                    const hasDiv = rem.divergencia !== 0 && !rem.estornada;
                    const acoes = (
                      <div className="flex items-center justify-center gap-2">
                            {permissoesDaSessao?.includes('producao:consultar') && !rem.estornada && <a className="inline-flex min-h-11 items-center text-xs font-medium text-blue-700 underline" href={`/producao/ordens/preparar?remessaId=${encodeURIComponent(rem.id)}`}>Preparar produção</a>}
                            {podeGerenciar && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedClienteForRetorno(rem.clienteId);
                                  setRetornoOpen(true);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 underline p-1.5 touch-manipulation"
                              >
                                Retornar
                              </button>
                            )}
                            {/* L-64: o botão só aparece para quem tem remessas:estornar; L-65: estorna o documento fiscal da linha. */}
                            <BotaoEstornarRemessa
                              permissoes={permissoesDaSessao}
                              documentoFiscalId={rem.documentoFiscalId}
                              estornada={rem.estornada}
                              onEstornar={() => void handleAbrirEstorno(rem.documentoFiscalId)}
                            />
                            {podeEstornarRemessas(permissoesDaSessao) && rem.documentoFiscalId === null && !rem.estornada ? (
                              <button type="button" onClick={() => void abrirEstornoManual(rem.id)} className="text-xs font-medium text-rose-600 underline">
                                Cancelar/estornar entrada
                              </button>
                            ) : null}
                      </div>
                    );
                    const divergencia = hasDiv ? (
                      <StatusIndicator tone="warning" label={`${rem.divergencia > 0 ? `+${rem.divergencia}` : rem.divergencia} un`} />
                    ) : rem.estornada ? <StatusIndicator tone="neutral" label="Recebimento estornado" /> : <StatusIndicator tone="success" label="Sem divergência" />;
                    return <ResponsiveTableRow
                      key={rem.id}
                      rowId={`remessa-${rem.id}`}
                      className="hover:bg-slate-50"
                      primaryCellIds={['cliente', 'item', 'fisica', 'acoes']}
                      cells={[
                        { id: 'cliente', label: 'Cliente', content: <span className="font-semibold text-slate-900">{rem.clienteId}</span> },
                        { id: 'item', label: 'Item / Material', content: <span className="break-words font-medium text-slate-700">{rem.itemId}</span> },
                        { id: 'lote', label: 'Lote do Cliente', content: <span className="font-mono text-slate-600">{rem.loteClienteRef}</span> },
                        { id: 'documento', label: 'Doc. Entrada', content: <span className="break-words text-slate-500">{rem.documentoEntrada || '-'}</span> },
                        { id: 'documental', label: 'Qtd Doc.', content: <span className="font-mono text-slate-600">{rem.qtdDocumental}</span>, className: 'text-right' },
                        { id: 'fisica', label: 'Qtd Física', content: <span className="font-mono font-bold text-slate-900">{rem.qtdFisica}<SeloEstorno estornada={rem.estornada} quantidadeEstornada={rem.quantidadeEstornada} /></span>, className: 'text-right' },
                        { id: 'divergencia', label: 'Divergência', content: divergencia },
                        { id: 'data', label: 'Data', content: <span className="text-xs text-slate-500">{new Date(rem.createdAt).toLocaleDateString('pt-BR')}</span> },
                        { id: 'acoes', label: 'Ações', content: acoes, className: 'text-center' },
                      ]}
                    />;
                  })}
                </tbody>
              </table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={total}
                itemLabel="remessas"
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setPage(1);
                }}
              />
            </div>
          )}
        </main>
      </div>

      {/* Modal Importar NF-e (Fase 10) */}
      {nfeModalOpen && (
        <CenteredDialog title={modoEntrada === 'chave' ? 'Consultar nota por chave' : 'Importar nota de entrada'} onClose={fecharModalNfe}>
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-slate-200 pb-3 pr-12">
              <h2 className="text-lg font-bold text-slate-900">Importar Documento Fiscal de Entrada (NF-e)</h2>
            </div>

            {nfeError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md" role="alert">
                {nfeError}
              </div>
            )}

            {!nfeRascunho ? (
              <form onSubmit={(e) => void handleImportarNfe(e)} className="space-y-4">
                {permissoesDaSessao?.includes('recebimentos:consultar') && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
                    <p>Para conferir uma nota já capturada da Omie, abra a lista de NF de remessa. Você também pode consultar uma chave específica ou importar o XML abaixo.</p>
                    <a href="/pcp/recebimentos" className="inline-flex min-h-11 items-center rounded font-semibold underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2">
                      Consultar NF de remessa (Omie)
                    </a>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-26216">Chave de Acesso NF-e (44 dígitos)</label>
                  <input id="field-remessas-page-26216"
                    autoFocus={modoEntrada === 'chave'}
                    type="text"
                    maxLength={44}
                    value={nfeChave}
                    onChange={(e) => setNfeChave(e.target.value)}
                    placeholder="35260812345678000195550010000012341000012345"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-26847">Ou cole o conteúdo XML da NF-e</label>
                  <textarea id="field-remessas-page-26847"
                    autoFocus={modoEntrada === 'xml'}
                    rows={6}
                    value={nfeXmlContent}
                    onChange={(e) => setNfeXmlContent(e.target.value)}
                    placeholder="<nfeProc xmlns=...><NFe>...</NFe></nfeProc>"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={fecharModalNfe}
                    className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded hover:bg-slate-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={nfeLoading}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {nfeLoading ? 'Processando...' : 'Carregar Rascunho'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 space-y-1">
                  <div className="flex justify-between items-center">
                    <div>
                      <div><strong>Chave:</strong> {nfeRascunho.chaveNfe}</div>
                      <div>
                        <strong>Estado:</strong> {nfeRascunho.estado}
                        {notaConfirmada ? '' : ' (Nenhum saldo foi gerado ainda — D-02)'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`/api/v1/documentos-fiscais/${nfeRascunho.id}/danfe`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2.5 py-1 bg-white border border-blue-300 text-blue-700 rounded text-xs font-medium hover:bg-blue-100 flex items-center gap-1"
                      >
                        <FileText className="w-3.5 h-3.5" /> Ver DANFE
                      </a>
                      {sessao?.autenticado &&
                        (sessao.usuario.role === 'Admin' ||
                          sessao.usuario.permissions.includes('remessas:ver-fiscal-completo')) && (
                          <a
                            href={`/api/v1/documentos-fiscais/${nfeRascunho.id}/xml`}
                            download
                            className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                          >
                            Baixar XML Fiscal
                          </a>
                        )}
                    </div>
                  </div>
                </div>

                <h3 className="text-sm font-bold text-slate-900">Conferência Física dos Itens e Conciliação Industrial</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {(nfeRascunho.itens ?? []).map((item) => {
                    const conf = conferencias[item.id] ?? { qtdFisica: String(item.quantidadeDocumental), materialId: item.descricao, loteClienteRef: '' };
                    return (
                      <div key={item.id} className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-semibold text-slate-900">Item {item.numeroItem}: {item.descricao}</span>
                          <span className="text-slate-500">Qtd Doc: <strong>{item.quantidadeDocumental} {item.unidadeFiscal}</strong></span>
                        </div>
                        {candidatosPv.disponivel && (
                          <ItemDoPvSelect
                            documentoFiscalItemId={item.id}
                            candidatos={candidatosPv.candidatosPorItem[item.id] ?? []}
                            valor={pvSelecao[item.id] ?? ''}
                            quantidadeFisica={parseFloat(conf.qtdFisica) || 0}
                            onChange={(valor) => setPvSelecao({ ...pvSelecao, [item.id]: valor })}
                            justificativa={pvJustificativas[item.id] ?? ''}
                            onJustificativaChange={(texto) => setPvJustificativas({ ...pvJustificativas, [item.id]: texto })}
                            divisao={pvDivisoes[item.id] ?? []}
                            onDivisaoChange={(divisao) => setPvDivisoes({ ...pvDivisoes, [item.id]: divisao })}
                          />
                        )}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <label className="block text-slate-600 mb-1">Qtd Física Conferida *</label>
                            <input
                              type="number"
                              value={conf.qtdFisica}
                              onChange={(e) => setConferencias({ ...conferencias, [item.id]: { ...conf, qtdFisica: e.target.value } })}
                              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-slate-600 mb-1">Material Industrial Vinculado</label>
                            <input
                              type="text"
                              value={(pvSelecao[item.id] ?? '') !== '' ? 'Definido pelo item do PV' : conf.materialId}
                              onChange={(e) => setConferencias({ ...conferencias, [item.id]: { ...conf, materialId: e.target.value } })}
                              readOnly={(pvSelecao[item.id] ?? '') !== ''}
                              placeholder="Código do material"
                              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm read-only:bg-slate-100 read-only:text-slate-500"
                            />
                          </div>
                          <div>
                            <label className="block text-slate-600 mb-1">Lote do Cliente (opcional)</label>
                            <input
                              type="text"
                              value={conf.loteClienteRef}
                              onChange={(e) => setConferencias({ ...conferencias, [item.id]: { ...conf, loteClienteRef: e.target.value } })}
                              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {notaConfirmada && (
                  <div role="alert" className="p-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded text-xs leading-relaxed space-y-2">
                    <p>{MENSAGEM_NOTA_JA_CONFIRMADA}</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={cienteNovoRecebimento} onChange={(e) => setCienteNovoRecebimento(e.target.checked)} />
                      {ROTULO_CIENTE_NOVO_RECEBIMENTO}
                    </label>
                  </div>
                )}
                {itensPvSemMaterial.length > 0 && (
                  <div role="alert" className="p-2.5 bg-rose-50 border border-rose-200 text-rose-800 rounded text-xs leading-relaxed space-y-1">
                    <p>{MENSAGEM_ITEM_PV_SEM_MATERIAL}</p>
                    <ul className="list-disc pl-4">
                      {itensPvSemMaterial.map((candidato) => (
                        <li key={candidato.pedidoVendaItemId}>
                          <a className="underline" href={`/vendas/pedidos/detalhe?id=${encodeURIComponent(candidato.pedidoVendaId)}`} target="_blank" rel="noreferrer">
                            {`${candidato.pvNumero} · item ${candidato.numeroItem}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setNfeRascunho(null)}
                    className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded hover:bg-slate-100"
                  >
                    Voltar
                  </button>
                  <AcoesPvDaNota documentoFiscalId={nfeRascunho.id} />
                  <button
                    type="button"
                    onClick={() => void handleConfirmarRecebimento()}
                    disabled={nfeLoading || confirmacaoBloqueada || aguardandoSugestoes || (notaConfirmada && !cienteNovoRecebimento)}
                    aria-busy={aguardandoSugestoes || undefined}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {nfeLoading ? 'Confirmando...' : aguardandoSugestoes ? 'Consultando PVs da nota…' : 'Confirmar Recebimento Físico'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </CenteredDialog>
      )}

      {/* Modal Nova Remessa Manual */}
      {modalOpen && (
        <CenteredDialog title="Nova remessa manual" onClose={() => setModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h2 className="text-lg font-bold text-slate-900">Registrar Nova Remessa Manual</h2>
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md" role="alert">
                {submitError}
                {submitAtalho && (permissoesBastam(submitAtalho.exige, permissoesDaSessao) ? (
                  <a className="mt-1 block text-blue-700 underline font-medium" href={submitAtalho.href}>{submitAtalho.rotulo}</a>
                ) : (
                  <span className="mt-1 block">{submitAtalho.rotulo}: peça a quem tenha as permissões {submitAtalho.exige.join(', ')}.</span>
                ))}
              </div>
            )}

            <form onSubmit={(e) => void handleCriarRemessa(e)} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-37988">Cliente *</label>
                <LookupCombobox id="field-remessas-page-37988"
                  label="Cliente"
                  endpoint="/api/v1/remessas/lookups/clientes"
                  required
                  value={clienteId}
                  onChange={(id) => { setClienteId(id); setItemId(''); setLoteClienteRef(''); }}
                />
                <p className="mt-1 text-xs text-slate-500">Abra a lista ou pesquise por nome ou código. São exibidos os clientes ativos do cadastro, incluindo os sincronizados com a Omie.</p>
                {avisoPendencia && (
                  <div className="mt-2 p-2.5 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-md flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>{avisoPendencia}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-38887">Item / Material *</label>
                {itemPorCodigo ? (
                  <input id="field-remessas-page-38887" type="text" required value={itemId}
                    onChange={(e) => { setItemId(e.target.value); setLoteClienteRef(''); }}
                    placeholder="Código informado pelo cliente"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                ) : (
                  <fieldset disabled={!clienteId}>
                    <LookupCombobox key={clienteId} id="field-remessas-page-38887" label="Item / Material"
                      endpoint={`/api/v1/remessas/lookups/materiais?clienteId=${encodeURIComponent(clienteId)}`} required value={itemId}
                      onChange={(id) => { setItemId(id); setLoteClienteRef(''); }} />
                  </fieldset>
                )}
                {!clienteId ? <p className="mt-1 text-xs text-slate-500">Escolha primeiro o cliente para ver somente as peças vinculadas a ele.</p> : null}
                <button type="button" disabled={!clienteId} className="mt-1 text-xs text-primary underline disabled:cursor-not-allowed disabled:text-slate-400"
                  onClick={() => { setItemPorCodigo(!itemPorCodigo); setItemId(''); setLoteClienteRef(''); }}>
                  {itemPorCodigo ? 'Pesquisar peça cadastrada' : 'Não encontrei a peça: informar código'}
                </button>
                <button type="button" disabled={!clienteId} className="ml-3 mt-1 text-xs text-primary underline disabled:cursor-not-allowed disabled:text-slate-400" onClick={() => setCadastroPecaOpen(true)}>Cadastrar ou vincular peça</button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-39496">Lote do Cliente *</label>
                  <input id="field-remessas-page-39496"
                    type="text"
                    required
                    value={loteClienteRef}
                    onChange={(e) => setLoteClienteRef(e.target.value)}
                    placeholder="Ex: LOT-2026-X"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-slate-500">Referência na etiqueta ou documento do cliente. O lote interno é gerado pelo sistema.</p>
                  {clienteId && itemId && (
                    <div className="mt-2">
                      <LookupCombobox key={`${clienteId}:${itemId}`} id="remessa-lote-anterior" label="Lote anterior"
                        placeholder="Consultar lotes anteriores"
                        emptyMessage="Nenhum lote anterior para este cliente e peça. Informe a referência do material recebido."
                        endpoint={`/api/v1/remessas/lookups/lotes-cliente?clienteId=${encodeURIComponent(clienteId)}&itemId=${encodeURIComponent(itemId)}`}
                        value="" valueKey="codigo" onChange={setLoteClienteRef} />
                      <p className="mt-1 text-xs text-slate-500">Confira a referência antes de usar um lote anterior.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-40075">Doc. Entrada / NF-e</label>
                  <a href="/pcp/recebimentos" target="_blank" rel="noopener noreferrer" className="mb-2 inline-flex min-h-11 items-center text-sm font-medium text-teal-700 underline">Buscar NF na Omie (nova aba)</a>
                  <p className="mb-2 text-xs text-slate-500">Abra a nota e confira os itens e quantidades para registrar a remessa com os dados da NF-e. O preenchimento manual desta janela fica preservado.</p>
                  <input id="field-remessas-page-40075"
                    type="text"
                    value={documentoEntrada}
                    onChange={(e) => setDocumentoEntrada(e.target.value)}
                    placeholder="Ex: NF-12345"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-40708">Qtd Documental *</label>
                  <input id="field-remessas-page-40708"
                    type="number"
                    required
                    min="1"
                    value={qtdDocumental}
                    onChange={(e) => setQtdDocumental(e.target.value)}
                    placeholder="100"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-remessas-page-41304">Qtd Física (Conferida) *</label>
                  <input id="field-remessas-page-41304"
                    type="number"
                    required
                    min="1"
                    value={qtdFisica}
                    onChange={(e) => setQtdFisica(e.target.value)}
                    placeholder="98"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Alerta inline não bloqueante de divergência (D-01) */}
              {temDivergencia && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded text-xs leading-relaxed">
                  <strong>Divergência registrada:</strong> {Math.abs(delta)} un a {delta > 0 ? 'mais' : 'menos'} que o
                  documento. A quantidade física prevalece e a divergência fica registrada para auditoria.
                </div>
              )}

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !clienteId || !itemId}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Registrando...' : 'Registrar remessa'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}

      {cadastroPecaOpen && clienteId ? <RemessaCadastroPecaDialog clienteId={clienteId} codigoInicial={itemPorCodigo ? itemId : ''} onClose={() => setCadastroPecaOpen(false)} onConcluida={(materialId) => { setItemId(materialId); setItemPorCodigo(false); setCadastroPecaOpen(false); }} /> : null}

      {estornoManualId !== null && <CenteredDialog title="Cancelar entrada manual" onClose={() => setEstornoManualId(null)}><form onSubmit={(event) => void executarEstornoManual(event)} className="w-full max-w-lg space-y-4 rounded-lg bg-white p-6 shadow-xl"><h2 className="text-lg font-bold">Cancelar/estornar entrada manual</h2>{erroEstornoManual ? <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erroEstornoManual}</p> : null}{impactoManual ? <><p className="text-sm">Saldo atual: <strong>{impactoManual.saldo} un</strong>.</p>{impactoManual.estornada ? <p className="text-sm">Esta entrada já foi estornada; nenhum novo movimento será criado.</p> : impactoManual.bloqueios.length > 0 ? <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">{impactoManual.bloqueios.map((bloqueio) => <li key={bloqueio}>{bloqueio}</li>)}</ul> : <><p className="text-sm">A confirmação cria um único ESTORNO integral e preserva a remessa, o lote e a auditoria.</p><label className="block text-sm font-medium">Justificativa<textarea required value={motivoEstornoManual} onChange={(e) => setMotivoEstornoManual(e.target.value)} className="mt-1 w-full rounded border p-2" /></label></>}</> : <p>Carregando análise…</p>}<div className="flex justify-end gap-3"><button type="button" onClick={() => setEstornoManualId(null)} className="rounded border px-4 py-2">Fechar</button>{impactoManual?.elegivel ? <button className="rounded bg-rose-700 px-4 py-2 text-white" disabled={!motivoEstornoManual.trim()}>Confirmar estorno</button> : null}</div></form></CenteredDialog>}

      {/* Modal Estorno Compensatório (REM-08 / D-30..D-33) */}
      {estornoModalOpen && (
        <CenteredDialog title="Estornar remessa" onClose={() => setEstornoModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Estorno Compensatório de Remessa</h2>
                <p className="text-xs text-slate-500">
                  O registro confirmado é imutável; a correção será gravada por movimentos inversos no ledger (D-30).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEstornoModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            {estornoSubmitError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md" role="alert">
                {estornoSubmitError}
              </div>
            )}

            {impactoLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">
                Analisando dependências e impacto no chão de fábrica...
              </div>
            ) : impactoError ? (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                {impactoError}
              </div>
            ) : impacto ? (
              <form onSubmit={(e) => void handleExecutarEstorno(e)} className="space-y-4">
                {/* Resumo de Saldos */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                  <div>
                    <span className="text-slate-500 block">Total Recebido Original:</span>
                    <strong className="text-slate-900 font-mono text-sm">{impacto.totalRecebido} un</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Saldo Atual no Ledger:</span>
                    <strong className="text-slate-900 font-mono text-sm">{impacto.saldoAtual} un</strong>
                  </div>
                </div>

                {/* Análise de Impacto (D-31) */}
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Análise de Impacto Guiada</h3>
                  
                  {impacto.naoIniciadas.length > 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs space-y-1">
                      <strong className="text-blue-900 block">Dependências não iniciadas (A cancelar automaticamente):</strong>
                      <ul className="list-disc pl-4 text-blue-800 space-y-0.5">
                        {impacto.naoIniciadas.map((dep, i) => (
                          <li key={i}>{dep.descricao}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {impacto.realizadas.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs space-y-1">
                      <strong className="text-amber-900 block">Dependências realizadas (Ajustes compensatórios):</strong>
                      <ul className="list-disc pl-4 text-amber-800 space-y-0.5">
                        {impacto.realizadas.map((dep, i) => (
                          <li key={i}>{dep.descricao}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {impacto.naoIniciadas.length === 0 && impacto.realizadas.length === 0 && (
                    <p className="text-xs text-slate-500">Nenhuma dependência de produção vinculada. Estorno direto no estoque.</p>
                  )}
                </div>

                {/* Alerta de Aprovação Reforçada (D-33) */}
                {impacto.exigeAprovacaoReforcada && (
                  <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-lg text-xs space-y-2">
                    <div className="flex items-center gap-2 text-rose-800 font-semibold">
                      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                      <span>Aprovação Reforçada Obrigatória (D-33)</span>
                    </div>
                    <p className="text-rose-700 leading-relaxed">
                      Como existem apontamentos ou retornos vinculados, o estorno exige autorização de um segundo usuário (Gestão/Admin) e justificativa detalhada.
                    </p>
                    <div>
                      <label className="block font-semibold text-rose-900 mb-1" htmlFor="field-remessas-page-48236">ID do Usuário Aprovador (Gestão/Admin) *</label>
                      <input id="field-remessas-page-48236"
                        type="text"
                        required
                        value={estornoAprovadorUserId}
                        onChange={(e) => setEstornoAprovadorUserId(e.target.value)}
                        placeholder="UUID do usuário aprovador"
                        className="w-full px-3 py-1.5 border border-rose-300 rounded text-xs bg-white focus:ring-2 focus:ring-rose-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* Escopo do Estorno */}
                <div>
                  <label htmlFor="estorno-escopo" className="block text-xs font-semibold text-slate-700 mb-1">Escopo do Estorno *</label>
                  <CampoSelect
                    id="estorno-escopo"
                    value={estornoEscopo}
                    onValueChange={(valor) => setEstornoEscopo(valor as EscopoEstorno)}
                    options={[
                      { value: 'TOTAL', label: 'Estorno Total (reverter quantidade total da remessa)' },
                      { value: 'PARCIAL', label: 'Estorno Parcial' },
                    ]}
                  />
                </div>

                {/* Motivo e Justificativa */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-semibold text-slate-700">
                      Motivo / Justificativa do Estorno * {impacto.exigeAprovacaoReforcada ? '(mínimo 15 chars)' : '(mínimo 5 chars)'}
                    </label>
                    <span className={`text-[10px] ${estornoMotivo.trim().length < (impacto.exigeAprovacaoReforcada ? 15 : 5) ? 'text-rose-600 font-bold' : 'text-slate-400'}`}>
                      {estornoMotivo.trim().length} caracteres
                    </span>
                  </div>
                  <textarea
                    required
                    rows={3}
                    value={estornoMotivo}
                    onChange={(e) => setEstornoMotivo(e.target.value)}
                    placeholder="Descreva detalhadamente o motivo do estorno para auditoria e ledger..."
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-rose-500 focus:outline-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setEstornoModalOpen(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded hover:bg-slate-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={estornoSubmitting || estornoMotivo.trim().length < (impacto.exigeAprovacaoReforcada ? 15 : 5)}
                    className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded hover:bg-rose-700 disabled:opacity-50 touch-manipulation"
                  >
                    {estornoSubmitting ? 'Processando Estorno...' : 'Confirmar Estorno Compensatório'}
                  </button>
                </div>
              </form>
            ) : null}
          </div>
        </CenteredDialog>
      )}

      {/* Painel Retorno */}
      <RetornoPanel
        isOpen={retornoOpen}
        clienteId={selectedClienteForRetorno}
        onClose={() => setRetornoOpen(false)}
        onRetornoConfirmado={() => void fetchRemessas()}
      />
    </div>
  );
}
